import {Component, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {SortTable} from '../../../../../../shared/models/sort-table/types';
import {App, AppId} from '../../../../../../shared/models/app';
import {AppsState} from '../../../../../../core/state/apps.state';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../../../app/app.state';
import {ActivatedRoute} from '@angular/router';
import {AppsService} from '../../../../../../core/services/apps.service';
import {UrlService} from '../../../../../../core/services/url.service';
import {UsersService} from '../../../../../../core/services/users.service';
import {User, UserId} from '../../../../../../shared/models/user';
import {SortTableCellEmpty, SortTableCellKebabMenu, SortTableCellTextPill} from '../../../../../../shared/models/sort-table/cell-classes';
import {ShadeToolsService} from '../../../../../../core/services/shade-tools.service';
import {ChildManageBaseComponent} from '../child-manage-base/child-manage-base.component';
import {SubnavComponent} from '../../../../../components/subnav/subnav.component';
import {SortTableComponent} from '../../../../../components/sort-table/sort-table.component';
import {ObjectType} from '../../../../../../shared/models/object.model';
import {PermissionsService} from 'src/core/services/permissions.service';
import {Role} from 'src/shared/models/fga/role';
import { DialogBoxButtonColor } from 'src/shared/models/dialog-box';

@Component({
  selector: 'motn-unit-users-prop-manager',
  templateUrl: './unit-users-prop-manager.component.html',
  styleUrls: ['./unit-users-prop-manager.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    SubnavComponent,
    SortTableComponent,
  ],
})
export class UnitUsersPropManagerComponent extends ChildManageBaseComponent implements OnInit, OnDestroy {
  app: App;
  appId: AppId;
  appUsers: User[];

  childApp: App;
  childAppId: AppId;
  childAppUsers: User[];

  table$ = new BehaviorSubject<SortTable>(null);


  constructor(
    protected route: ActivatedRoute,
    protected appsService: AppsService,
    protected shadeTools: ShadeToolsService,
    protected store: Store<AppState>,
    protected urlService: UrlService,
    private usersService: UsersService,
    private permissionsService: PermissionsService,
  ) {
    super(appsService, urlService, shadeTools, route, store);
  }


  protected onInit(): void {
    this.appsSub = this.store.select('apps').subscribe(
      (data: AppsState) => {
        this.app = data.apps.find((a) => {
          return a.ID === this.appId;
        });

        this.childApp = data.apps.find((a) => {
          return a.ID === this.childAppId;
        });

        if (this.app) {
          if (!this.appUsers) {
            this.appUsers = [];
            this.updateAppUsers();
          }
        }

        if (this.childApp) {
          this.updateTitle();

          if (!this.childAppUsers) {
            this.childAppUsers = [];
            this.updateChildAppUsers();
          }
        }
      },
    );
  }


  protected onDestroy() {
    this.table$?.complete();
  }


  private updateAppUsers() {
    this.usersService.findUsers$('AppID', '' + this.appId).subscribe(
      (users) => {
        this.appUsers = users;
        this.updateTable();
      },
    );
  }


  private updateChildAppUsers() {
    this.usersService.findUsers$('AppID', '' + this.childAppId).subscribe(
      (users) => {
        this.childAppUsers = users;
        this.updateTable();
      },
    );
  }


  addUser(): void {
    this.shadeTools.startDialog(
      'Add Tenant User',
      'Enter the Motion username of the user you wish to add as a tenant or guest of this unit.',
      [
        {
          text: 'Add',
          callback: (input) => this.handleAddUser(input),
        },
        {
          text: 'Cancel',
          callback: () => this.shadeTools.closeAll(),
          color: DialogBoxButtonColor.Alt,
        },
      ],
      true,
      false,
      false,
      false,
    );
  }


  private handleAddUser(input: string): void {
    const trimmedInput = input.trim();

    this.usersService.findUser$('Username', trimmedInput).subscribe({
      next: (user) => this.assignRoleToUser(user.ID),
      error: () => this.shadeTools.errorDialog('No user with that username could be found.'),
    });
  }


  private assignRoleToUser(userId: UserId): void {
    const roleAssignment = {
      UserID: userId,
      ObjectID: this.childAppId,
      ObjectType: ObjectType.APP,
      Roles: [Role.VALUE_WRITER],
    };

    this.permissionsService.assignRolesToUser(roleAssignment).subscribe({
      next: () => this.onRoleOperationSuccess(),
      error: () => this.shadeTools.errorDialog(),
    });
  }


  private onRoleOperationSuccess(): void {
    this.updateChildAppUsers();
    this.shadeTools.closeAll();
  }


  private removeUser(userId: UserId): void {
    const user = this.childAppUsers.find(u => u.ID === userId);
    if (!user) {
      return;
    }

    this.shadeTools.startDialog(
      'Remove Tenant User',
      `Are you sure you want to remove ${user.Name}?`,
      [
        {
          text: 'Remove',
          callback: () => this.confirmRemoveUser(user),
          color: DialogBoxButtonColor.Red,
        },
        {
          text: 'Cancel',
          callback: () => this.shadeTools.closeAll(),
          color: DialogBoxButtonColor.Alt,
        },
      ],
      false,
      false,
      false,
      false,
    );
  }


  private confirmRemoveUser(user: User): void {
    const roleAssignment = {
      UserID: user.ID,
      ObjectID: this.childAppId,
      ObjectType: ObjectType.APP,
      Roles: [],
    };

    this.permissionsService.setUserRoles(roleAssignment).subscribe({
      next: () => this.onRoleOperationSuccess(),
      error: () => this.shadeTools.errorDialog(),
    });
  }


  private updateTable(): void {
    if (!this.appUsers || !this.childAppUsers) {
      return;
    }

    const table: SortTable = {
      css: {
        'min-width': '500px',
      },
      columns: [
        {
          name: 'Name',
          css: {
            'width': 'calc(50% - 25px)',
            // 'color': 'var(--motn-color-indigo)',
          },
        },
        {
          name: 'Motion Username',
          css: {
            'width': 'calc(50% - 25px)',
          },
        },
        {
          name: '',
          css: {
            'width': '50px',
            'justify-content': 'right',
          },
        },
      ],
      rows: [],
    };

    for (let user of this.childAppUsers) {
      // staff users include owner and grantees
      let staffUser = this.appUsers.find(
        (u) => {
          return u.ID === user.ID;
        },
      );

      let ownerUser = this.appUsers.find(
        (u) => {
          return u.ID === this.app.Owner;
        },
      );

      let userIsStaff = staffUser && user.ID === staffUser.ID;
      let userIsOwner = ownerUser && user.ID === ownerUser.ID;

      table.rows.push({
        context: '' + user.ID,
        cells: [
          new SortTableCellTextPill(user.Name, ''),
          new SortTableCellTextPill(user.Username, userIsStaff ? (userIsOwner ? 'App Owner' : 'Staff') : 'Tenant'),
          userIsStaff ? new SortTableCellEmpty() : new SortTableCellKebabMenu(user.ID, [
            {
              id: '0',
              name: 'Remove User',
              callback: () => {
                this.removeUser(user.ID);
              },
            },
          ], '', false, 1),
        ],
      });
    }

    this.table$.next(table);
  }
}
